import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useInView } from '../components/UI';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const milestones = [
  { year: '2026', desc: '$1.53M, 500 bikes, Phase 1 launch' },
  { year: '2028', desc: '$14.3M, 5,000 bikes, national expansion begins' },
  { year: '2030', desc: '$57.0M, 20,000 bikes, Azura Ride live' },
  { year: '2036', desc: '$264.8M, 100,000 bikes across 5 African markets' },
  { year: '2045', desc: '$1.28B, 500,000 bikes plus Sankofa OEM revenue additive' },
];

const heroStats = [
  { v: '$1.53M', l: 'Year 1 revenue, 500 bikes at launch' },
  { v: '$57.0M', l: 'Revenue by 2030, 20,000 EVs deployed' },
  { v: '$264.8M', l: 'Revenue by 2036, 100,000 EVs, 5 markets' },
  { v: '$3.3B+', l: 'Total annual revenue potential by 2045' },
];

const revenueRows = [
  { year: '2026', fleet: '500 bikes', stations: '17', fleetRev: '$795.6K', baas: '$324.5K', total: '$1.53M' },
  { year: '2027', fleet: '1,500 bikes', stations: '50', fleetRev: '$2.39M', baas: '$973K', total: '$4.38M' },
  { year: '2028', fleet: '5,000 bikes', stations: '167', fleetRev: '$7.96M', baas: '$3.24M', total: '$14.3M' },
  { year: '2029', fleet: '10,000 bikes', stations: '334', fleetRev: '$15.9M', baas: '$6.49M', total: '$28.5M' },
  { year: '2030', fleet: '20,000 bikes', stations: '667', fleetRev: '$31.8M', baas: '$13.0M', total: '$57.0M' },
  { year: '2036', fleet: '100,000 bikes', stations: '3,334', fleetRev: '$159.1M', baas: '$64.9M', total: '$264.8M' },
  { year: '2045', fleet: '500,000 bikes', stations: '16,667', fleetRev: '$795.6M', baas: '$324.5M', total: '$1.28B' },
];

const phases = [
  { phase: 'Phase 1', year: '2026', capex: '$1.38M', desc: '500 bikes · 17 stations', coverage: 'AZT Strategic + Presale ($1.5M)' },
  { phase: 'Phase 2', year: '2028', capex: '$13.7M', desc: '5,000 bikes · 167 stations', coverage: 'Operating revenue + expansion fund' },
  { phase: 'Phase 3', year: '2030', capex: '$54.7M', desc: '20,000 bikes · 667 stations', coverage: 'Operating revenue + DFI + climate finance' },
  { phase: 'Phase 4', year: '2036+', capex: '$273M+', desc: '100K+ bikes · 3,334+ stations', coverage: 'Operating revenue + strategic partnerships' },
];

const tokenMeta = [
  { label: 'Token Name', value: 'Azura Token' },
  { label: 'Symbol', value: 'AZT' },
  { label: 'Total Supply', value: '1,000,000,000' },
  { label: 'Inflation', value: 'None (fixed supply)' },
  { label: 'Token Type', value: 'Utility Token' },
  { label: 'Burn Mechanism', value: '10% of gross revenue quarterly' },
  { label: 'Strategic Round', value: '25,000 units @ $20/unit' },
  { label: 'AZT per unit', value: '4,000 AZT' },
  { label: 'Price per AZT', value: '$0.005' },
  { label: 'Total Raise Target', value: '$1.5M across 3 rounds' },
];

const rounds = [
  {
    name: 'Strategic Round',
    tokens: '100M AZT',
    price: '$20 per unit (4K AZT)',
    priceLabel: 'Per unit',
    raise: '$500K',
    raiseLabel: 'Capital raised',
    use: 'Phase 1 launch, BaaS station infrastructure, technology platform, operational setup.',
    highlight: true,
  },
  {
    name: 'Presale',
    tokens: '100M',
    price: '$0.010',
    priceLabel: 'Per token',
    raise: '$1.0M',
    raiseLabel: 'Capital raised',
    use: 'Fleet expansion, BaaS network rollout, rider acquisition and onboarding programme.',
    highlight: false,
  },
  {
    name: 'Public Launch',
    tokens: '100M',
    price: '$0.020',
    priceLabel: 'Per token',
    raise: '$2.0M',
    raiseLabel: 'Capital raised',
    use: 'National expansion, marketing and brand building, Azura Ride platform development.',
    highlight: false,
  },
];

const Img: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => (
  <img src={src} alt={alt} className={className}
    onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
);

/* ══ OPPORTUNITY PAGE ══ */
const Opportunity: React.FC = () => {
  const { ref: revRef, inView: revInView } = useInView();
  const { ref: capRef, inView: capInView } = useInView();
  const { ref: aztRef, inView: aztInView } = useInView();
  const { ref: roundRef, inView: roundInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)', backgroundSize: '70px 70px' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/30">/</span>
            <span className="text-accent-amber text-xs font-semibold">The Opportunity</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-4">
            The Opportunity.<br />
            <span className="text-accent-amber">From $1.53M to $1.28B.</span>
          </h1>
        
          <p className="max-w-2xl text-white/70 text-base leading-8 mb-10">
          Each phase of growth is financed by the operating revenue of the phase before it, not by a single external round.
          </p>

          <div className="flex flex-wrap gap-3 mb-14">
            <a href="/invest"
              className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all ">
              Invest Now <ArrowRight size={13} />
            </a>
            <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
              Download White Paper ↗
            </a>
          </div>

          {/* milestone timeline */}
{/*           <div className="relative">
            <div className="hidden md:block absolute top-5 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-amber/30 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {milestones.map((m, i) => (
                <div key={m.year} className="relative text-center md:text-left">
                  <div className="w-10 h-10 rounded-full bg-accent-amber/10 border border-accent-amber/30 flex items-center justify-center mx-auto md:mx-0 mb-3 font-figtree font-black text-accent-amber text-sm relative z-10">
                    {m.year.slice(-2)}
                  </div>
                  <p className="text-xs text-accent-amber/60 uppercase tracking-widest mb-1">{m.year}</p>
                  <p className="font-exo text-xs text-white/50 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </section>


      {/* ══ REVENUE TRAJECTORY ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={revRef} className="max-w-7xl mx-auto">
          <div className={`mb-12 transition-all duration-700 ${revInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Revenue Trajectory</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-3">
              Fleet, BaaS, and EV sales combined.<br />
              <span className="text-accent-amber italic">Three core recurring revenue streams.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* table */}
            <div className="lg:col-span-8">
              <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
                <div className="grid grid-cols-5 border-b border-border px-6 py-4 bg-neutral-50">
                  {['Year', 'Fleet Size', 'Stations', 'Fleet Rev', 'Total'].map(h => (
                    <span key={h} className="text-xs font-bold uppercase tracking-widest text-neutral-500">{h}</span>
                  ))}
                </div>
                {revenueRows.map((row, i) => (
                  <div key={row.year}
                    className={`grid grid-cols-5 px-6 py-4 border-b border-border last:border-0 hover:bg-neutral-50 transition-colors ${revInView ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: `${i * 50}ms`, transition: 'opacity 0.5s ease' }}>
                    <span className="font-figtree font-black text-sm text-accent-amber">{row.year}</span>
                    <span className="font-exo text-xs text-neutral-600">{row.fleet}</span>
                    <span className="font-exo text-xs text-neutral-600">{row.stations}</span>
                    <span className="font-exo text-xs text-neutral-600">{row.fleetRev}</span>
                    <span className="font-figtree font-black text-sm text-bg-dark">{row.total}</span>
                  </div>
                ))}
              </div>
              <p className="font-exo text-xs text-neutral-400 leading-relaxed mt-3 px-1">
                Base case with 15% default buffer on fleet revenue. Total includes fleet remittance, BaaS, and EV sales. Ride-hailing, carbon credits, and Sankofa manufacturing revenues are additive.
              </p>
            </div>

            {/* image */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-4">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] shadow-dark border border-border group">
                <Img src="/images/bike1.png" alt="Revenue trajectory" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="font-figtree font-black text-4xl text-accent-amber mb-1">$1.28B</p>
                  <p className="text-xs text-white/60 uppercase tracking-widest">Projected annual revenue · 2045</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      
      {/* hero stats strip */}
      <div className="bg-bg-dark border-t border-white/[0.06] border-b-4 border-b-accent-amber">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {heroStats.map((s, i) => (
              <div key={s.l} className="px-6 py-6 border-r border-white/[0.06] last:border-r-0">
                <p className="font-figtree font-black text-2xl text-accent-amber mb-1 tracking-tight">{s.v}</p>
                <p className="text-xs text-white/35 uppercase tracking-[0.15em] leading-snug">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ CAPITAL REQUIREMENTS ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={capRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${capInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Capital Requirements</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-3">
              Phased deployment.<br />
              <span className="text-accent-amber italic">Each stage self-funded by the one before.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              {/* connector line */}
              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-gradient-to-b from-accent-amber/60 via-accent-amber/20 to-transparent" />
                <div className="space-y-5">
                  {phases.map((p, i) => (
                    <div key={p.phase}
                      className={`flex gap-5 items-start pl-12 relative transition-all duration-700 ${capInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                      style={{ transitionDelay: `${i * 100}ms` }}>
                      <div className="absolute left-0 w-10 h-10 rounded-full bg-bg-dark flex items-center justify-center font-bold text-accent-amber text-xs shrink-0 z-10">
                        {p.year.slice(0, 4)}
                      </div>
                      <div className="bg-white border border-border rounded-2xl p-6 shadow-card flex-1 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <p className="text-xs text-accent-amber uppercase tracking-widest mb-1">{p.phase} · {p.year}</p>
                            <h3 className="font-figtree font-black text-lg text-bg-dark">{p.desc}</h3>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-figtree font-black text-2xl text-accent-amber tracking-tight">{p.capex}</p>
                            <p className="text-[8px] text-neutral-400 uppercase tracking-widest">Total capex</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pt-3 border-t border-border">
                          <CheckCircle2 size={12} className="text-bg-dark shrink-0" />
                          <p className="font-exo text-xs text-neutral-500">{p.coverage}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-dark border border-border group mb-5">
                <Img src="/images/bike3.png" alt="Capital requirements" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="bg-accent-amber text-bg-dark text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">Self-funding model</span>
                </div>
              </div>
              <div className="bg-bg-dark rounded-2xl p-6 text-center">
                <p className="font-exo text-white/50 text-xs leading-relaxed mb-4">
                  Phase 1 is fully covered by AZT Strategic + Presale proceeds of $1.5M, against a capex requirement of $1.38M, with a $123K operational buffer.
                </p>
                <a href="mailto:invest@azuramobility.com"
                  className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-5 py-2.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all ">
                  Request Briefing <ArrowRight size={12} />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ AZT TOKEN ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white relative overflow-hidden border-t-4 border-accent-amber">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div ref={aztRef} className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className={`lg:col-span-6 transition-all duration-700 ${aztInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">AZT Utility Token</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5">
                Community capital,<br />
                <span className="text-accent-amber italic">backed by real infrastructure.</span>
              </h2>
              <p className="font-exo text-white/55 text-sm leading-relaxed mb-8">
                AZT is not a speculative asset. It is a utility token backed by physical infrastructure: deployed EVs, battery swap stations, and operating revenue. 10% of all gross operational revenue is allocated to quarterly token burn, progressively reducing circulating supply as the fleet scales.
              </p>

              {/* token meta grid */}
              <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden">
                {tokenMeta.map((m, i) => (
                  <div key={m.label} className="flex justify-between px-5 py-3.5 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] transition-colors">
                    <span className="text-xs text-white/35 uppercase tracking-widest">{m.label}</span>
                    <span className="font-figtree font-bold text-sm text-white">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden aspect-video shadow-dark border border-white/10 group mb-5">
                <Img src="/images/bike2.png" alt="AZT Token" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-br from-bg-dark/80 to-accent-amber/10" />
                <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                  <div>
                    <p className="font-figtree font-black text-5xl text-accent-amber mb-2">AZT</p>
                    <p className="text-xs text-white/40 uppercase tracking-widest">1,000,000,000 · Fixed supply</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Strategic', price: '$0.005', desc: 'Per AZT' },
                  { label: 'Presale', price: '$0.010', desc: 'Per AZT' },
                  { label: 'Public', price: '$0.020', desc: 'Per AZT' },
                ].map(r => (
                  <div key={r.label} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-center">
                    <p className="text-xs text-white/35 uppercase tracking-widest mb-1">{r.label}</p>
                    <p className="font-figtree font-black text-xl text-accent-amber">{r.price}</p>
                    <p className="text-[8px] text-white/30 mt-0.5">{r.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ FUNDRAISING ROUNDS ══ */}
      <section className="py-20 px-6 bg-bg-cream border-t border-border">
        <div ref={roundRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${roundInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Fundraising Rounds</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              Three rounds. <span className="text-accent-amber italic">One trajectory.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {rounds.map((r, i) => (
              <div key={r.name}
                className={`relative rounded-2xl overflow-hidden border shadow-card transition-all duration-700 ${r.highlight ? 'bg-bg-dark border-accent-amber/30' : 'bg-white border-border'} ${roundInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                {r.highlight && (
                  <div className="absolute top-4 right-4">
                    <span className="text-[8px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-400">Open</span>
                  </div>
                )}

                <div className="p-7">
                  <p className={`text-xs uppercase tracking-widest mb-2 ${r.highlight ? 'text-accent-amber/60' : 'text-neutral-400'}`}>{r.name}</p>
                  <p className={`font-figtree font-black text-3xl mb-1 tracking-tight ${r.highlight ? 'text-accent-amber' : 'text-accent-amber'}`}>{r.tokens}</p>
                  <p className={`text-xs mb-5 ${r.highlight ? 'text-white/30' : 'text-neutral-400'}`}>AZT tokens</p>

                  <div className="grid grid-cols-2 gap-3 mb-5 pt-5 border-t border-white/[0.06]">
                    <div>
                      <p className={`font-figtree font-black text-xl ${r.highlight ? 'text-white' : 'text-bg-dark'}`}>{r.price}</p>
                      <p className={`text-[8px] uppercase tracking-widest ${r.highlight ? 'text-white/30' : 'text-neutral-400'}`}>{r.priceLabel}</p>
                    </div>
                    <div>
                      <p className={`font-figtree font-black text-xl ${r.highlight ? 'text-accent-amber' : 'text-accent-amber'}`}>{r.raise}</p>
                      <p className={`text-[8px] uppercase tracking-widest ${r.highlight ? 'text-white/30' : 'text-neutral-400'}`}>{r.raiseLabel}</p>
                    </div>
                  </div>

                  <p className={`font-exo text-xs leading-relaxed ${r.highlight ? 'text-white/60' : 'text-neutral-500'}`}>{r.use}</p>
                </div>
              </div>
            ))}
          </div>

          {/* structure note */}
          <div className="bg-white border border-border rounded-2xl p-7 shadow-card mb-5">
            <div className="flex gap-4">
              <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
              <p className="font-exo text-sm text-neutral-600 leading-relaxed">
                Structured as 25,000 units at $20 per unit, with each unit containing 4,000 AZT, totalling 100M AZT at $0.005 per token. Combined with Presale proceeds, this raises $1.5M, comfortably covering Phase 1 capex of $1.38M with a $123K operational buffer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ STRATEGIC ROUND CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber relative overflow-hidden">
       
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <span className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-5">— Strategic Round · Open Now</span>
          <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5 leading-tight">
            The Strategic Round is open.<br />
            <span className="text-accent-amber italic">100M AZT at $0.005.</span>
          </h2>
          <p className="font-exo text-white/55 text-base leading-relaxed mb-10 max-w-2xl mx-auto">
            This is the earliest entry point into Azura's capital structure. Strategic Round investors participate at the founding price, before the fleet launches, before BaaS stations are operational, and before the brand is established in market.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="/invest"
              className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all ">
             Invest now <ArrowRight size={13} />
            </a>
            <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
              Download White Paper ↗
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Opportunity;